import type { Anomaly, GameState, Meeting, MeetingResult, Seat, ShowState } from './types.js';

/**
 * The saboteur game, played over dinner.
 *
 * Up to two guests are secretly Paradox Agents. Their game of the stop is the sabotage; once every
 * game of the stop is done, each one's sabotage sets off an alarm, one after the other, that the TV
 * traces to a stretch of the table (stop.ts, Marek 2026-10-06: one thing at a time). The room hands
 * FIX to as many guests as the era needs: after a ten-second countdown each of them gets a short
 * puzzle on their phone, and once all but one of them have solved it the alarm is fixed (Marek,
 * 2026-09-29). Everyone else sees the alarm and can tell them to hurry. A called phone that is asleep
 * is replaced by the next seat. Left unsolved for 40 seconds it does damage, though that time stands
 * still while the table is in a meeting. Then the stop meets by itself: everyone picks the guest they
 * suspect, the most votes is voted out, the stop's saboteurs are revealed and the vote is scored;
 * nothing is repaired for it.
 *
 * Damage adds up over the whole voyage and is never reset, up to a ceiling of four of the ship's five
 * boxes: the ship always gets home. At the ceiling an alarm still plays out as usual, and one that runs
 * out unfixed quietly counts as fixed. The machines' takeover at the end of 2213 is two damage of its
 * own. When the ship docks there is no winner: it comes home, with the damage it took (Marek,
 * 2026-09-29: "no winner, just the damage"), and the scores decide the rest.
 *
 * Nobody is made an agent against their will. Guests say at check-in whether they would play
 * one, and the room draws from those who said yes when the ship launches — and again at every
 * stop after it, so each era has new saboteurs and more of the table gets a turn (Marek,
 * 2026-09-28). If too few said yes, the timeline itself stands in for the missing agents, so the
 * crew's evening plays the same whatever the draw found, and nobody can work out the count from
 * how it feels.
 *
 * Pure rules only. The reducer owns the seats and the voyage log.
 */
export const GAME = {
  /** The ship's boxes on every screen. It never fills them all: see `DAMAGE_CEILING`. */
  FRACTURE_LIMIT: 5,
  /**
   * The most damage the ship ever takes, all evening (Marek, 2026-09-29): it always gets home with at
   * least one box left. Damage adds up over all five stops and is never reset.
   */
  DAMAGE_CEILING: 4,
  /** Catching a saboteur repairs nothing (Marek, 2026-09-29). */
  HEAL_PER_AGENT: 0,
  /** The machines' takeover at the end of 2213 is damage of its own. */
  MACHINE_DAMAGE: 2,
  /**
   * Per agent, per era: one, and it is their game of the stop (Marek, 2026-10-06: the saboteurs play
   * their sabotage while everyone else plays, and each sets off one alarm once the games are done).
   */
  SABOTAGES_PER_CHAPTER: 1,
  /** The sabotage game's par, scored like any other game. */
  SABOTAGE_PAR_MS: 30_000,
  /** Between any two anomalies, so the table is never fighting two at once. */
  SABOTAGE_GAP_MS: 120_000,
  /**
   * FIX MISSION IN 10 … 1: an alarm opens with a countdown on the called phones, the TV and the table
   * before the puzzle appears (Marek, 2026-09-29). It is part of the alarm's time.
   */
  FIX_COUNTDOWN_MS: 10_000,
  /** How long the called crew have to solve the FIX puzzle, after the countdown (2213 too). */
  STABILISE_WINDOW_MS: 40_000,
  /** How many are handed FIX; all but one of them must solve it (`solvesNeeded`). */
  STABILISE_CREW: 2,
  /**
   * A phone handed FIX that has not shown the alarm by now is taken to be asleep, and the next
   * seat is called as well. Answered but not solving is not asleep: nobody else is called for
   * that, because a guest who was called and never solved is worth talking about.
   */
  FIX_ANSWER_MS: 10_000,
  /** A meeting closes itself after this long. */
  MEETING_MS: 150_000,
  /** The stop's own meeting, where everyone picks a suspect: talk and vote in this long, or less once all have voted. */
  PICK_MEETING_MS: 90_000,
  /** The fewest votes that send a guest out in that meeting. */
  PICK_MIN_VOTES: 2,
  /** A guest may call a meeting from when an alarm shows its trace until this long after it ends. */
  MEETING_AFTER_ALARM_MS: 120_000,
  /** Seats a trace names: wide enough to suspect, never narrow enough to be sure. */
  TRACE_WIDTH: 4,
  /**
   * Where in an era's games the timeline acts for a missing agent: once this share of the stop's
   * games is done, or this long after the quiet start, whichever comes first. The games are the
   * round now (Marek, 2026-09-28), so the timeline keeps to them rather than to the clock.
   */
  ROGUE_AT: [0.25, 0.6],
  ROGUE_AFTER_MS: [45_000, 180_000],
  /** 2213: where the machines raise their own alarm, in the same terms — late, as they rise. */
  MACHINE_AT: [0.5],
  MACHINE_AFTER_MS: [240_000],
  /**
   * A saboteur who has not struck this era by the time this share of the games is done, or this long
   * into them, is told on their own phone to strike now (Marek, 2026-09-28: the ship never took damage).
   */
  NUDGE_AT: 0.5,
  NUDGE_AFTER_MS: 150_000,
} as const;

/**
 * 2213, the neon era, belongs to the saboteurs, and they cannot lose it: by winning they set the
 * machines free, and the next era is the apocalypse. The table still plays for it, so the era
 * only tilts the rules their way. When its games are done the machines take the ship, which costs
 * `MACHINE_DAMAGE` (the reducer's `machinesRise`).
 */
export const MACHINE_ERA = 2;

export interface EraRules {
  sabotagesPerChapter: number;
  sabotageGapMs: number;
  stabiliseCrew: number;
  stabiliseWindowMs: number;
  /** Damage repaired for catching a real saboteur: none, since 2026-09-29. */
  healPerAgent: number;
  /** Alarms the era raises by itself, on top of any saboteur the draw could not find. */
  machineAlarms: number;
  /**
   * No alarm for this long after the games open, so the call to play is heard and every phone gets
   * its game started first (Marek, 2026-09-28: in 2213 the alarms covered the phones).
   */
  quietStartMs: number;
  /** Alarms only while the games are on, never over the pour or the plate. */
  gamesOnly: boolean;
}

export function eraRules(chapter: number): EraRules {
  if (chapter === MACHINE_ERA) {
    // Calmer than it was (2026-09-28): two sabotages each, not four; one machine alarm, not two, and
    // late; nothing for the first two minutes of the games. The machines still take the ship at the end.
    return {
      sabotagesPerChapter: GAME.SABOTAGES_PER_CHAPTER,
      sabotageGapMs: 90_000,
      stabiliseCrew: 3,
      stabiliseWindowMs: GAME.STABILISE_WINDOW_MS,
      healPerAgent: 0,
      // The machines no longer raise an alarm of their own: they take the ship after the saboteurs' alarms.
      machineAlarms: 0,
      quietStartMs: 120_000,
      gamesOnly: true,
    };
  }
  return {
    sabotagesPerChapter: GAME.SABOTAGES_PER_CHAPTER,
    sabotageGapMs: GAME.SABOTAGE_GAP_MS,
    stabiliseCrew: GAME.STABILISE_CREW,
    stabiliseWindowMs: GAME.STABILISE_WINDOW_MS,
    healPerAgent: GAME.HEAL_PER_AGENT,
    machineAlarms: 0,
    quietStartMs: 45_000,
    gamesOnly: false,
  };
}

export function emptyGame(): GameState {
  return {
    drawn: false,
    fractures: 0,
    anomalies: [],
    meeting: null,
    meetings: [],
    sabotagesUsed: {},
    meetingsCalled: [],
    rogue: 0,
    rogueFired: {},
    outcome: null,
    draws: 0,
    agentStops: {},
    round: null,
    uprising: null,
    secondFight: false,
    armed: {},
    stop: null,
    reveals: [],
  };
}

/** Two agents at a full table, one at a small one: counted in guests actually playing (`playersAboard`). */
export function agentsWanted(players: number): number {
  return players >= 8 ? 2 : 1;
}

/**
 * How many are playing tonight: guests aboard with a post. Guests can be added and checked out in
 * the middle of the evening (Marek, 2026-09-28: "the whole game mechanics should be changed
 * automatically to a new amount of players"), so every count the game makes starts here. Before
 * anyone has scanned in, the places laid.
 */
export function playersAboard(state: Pick<ShowState, 'crewSize' | 'seats'>): number {
  const aboard = state.seats.filter(isCrewed).length;
  return aboard > 0 ? aboard : state.crewSize;
}

/**
 * How many are handed FIX for an alarm raised now: the era's number, but never more than all but one
 * of those who could (aboard and not voted out). Kept on the alarm (`need`), so it does not move
 * under the people solving it.
 */
export function fixersNeeded(state: Pick<ShowState, 'seats'>, chapter: number): number {
  const able = state.seats.filter((s) => isCrewed(s) && !s.brig).length;
  return Math.max(1, Math.min(eraRules(chapter).stabiliseCrew, able - 1));
}

/** How many an alarm hands FIX to: its own number, or the era's for an alarm from before that. */
export function fixNeed(a: Pick<Anomaly, 'chapter'> & { need?: number }): number {
  return a.need ?? eraRules(a.chapter).stabiliseCrew;
}

/**
 * How many solved FIX puzzles fix an alarm: all but one of those handed it, and at least one (Marek,
 * 2026-09-29) — so a single saboteur among them cannot block it alone. A seat called later for a
 * sleeping phone does not raise it.
 */
export function solvesNeeded(a: Pick<Anomaly, 'chapter'> & { need?: number }): number {
  return Math.max(1, fixNeed(a) - 1);
}

/** When an alarm's FIX puzzle appears: after the countdown. A meeting moves it on with the deadline. */
export function fixOpensAt(a: Pick<Anomaly, 'chapter' | 'deadline'>): number {
  return a.deadline - eraRules(a.chapter).stabiliseWindowMs;
}

/** The three FIX puzzles (Marek, 2026-09-29). */
export type FixPuzzle = 'circuit' | 'code' | 'order';
export const FIX_PUZZLES: readonly FixPuzzle[] = ['circuit', 'code', 'order'];

/**
 * Which puzzle a called seat gets for an alarm, and the number its content is made from: chosen per
 * alarm and seat, and the same every time it is asked, so a phone that reloads shows the same one.
 */
export function fixPuzzleFor(voyageId: string, anomalyId: string, seat: number): { kind: FixPuzzle; seed: number } {
  const seed = mix(voyageId, 'fix-puzzle', anomalyId, seat);
  return { kind: FIX_PUZZLES[seed % FIX_PUZZLES.length]!, seed };
}

/** Damage added, never past the ceiling; a room already past it (from before the ceiling) keeps its count. */
export function withDamage(fractures: number, add: number): number {
  return Math.max(fractures, Math.min(GAME.DAMAGE_CEILING, fractures + add));
}

function mix(...parts: (string | number)[]): number {
  let h = 0x811c9dc5;
  const s = parts.join(':');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * `mix`, stirred once more so the last character moves every bit. Plain `mix` of a seat number put
 * seats in nearly the same order whatever came before it, so the draw's ties went to seats 8–10
 * almost every stop (found 2026-10-06).
 */
function stir(...parts: (string | number)[]): number {
  let h = mix(...parts);
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

/** A seat someone is actually sitting in, with a post. */
export function isCrewed(seat: Seat): boolean {
  return seat.token !== null && seat.profession !== null;
}

/**
 * When the game can be played: once a pour has landed, and never over a film, a jump or the
 * one photo call on Earth.
 */
export function gameInPlay(state: Pick<ShowState, 'chapter' | 'phase'>): boolean {
  if (state.chapter < 1 || state.chapter > 5) return false;
  return state.phase === 'serve' || state.phase === 'signal' || state.phase === 'course' || state.phase === 'fieldwork';
}

/**
 * How long is left of the quiet at the start of the games, when no alarm may go off (`quietStartMs`);
 * 0 once it is over, or outside the games.
 */
export function alarmQuietLeft(state: Pick<ShowState, 'chapter' | 'phase' | 'phaseStartedAt'>, now: number): number {
  if (state.phase !== 'fieldwork') return 0;
  return Math.max(0, state.phaseStartedAt + eraRules(state.chapter).quietStartMs - now);
}

/**
 * Whether an alarm may go off now, by the clock of the evening: the game is in play, this era allows
 * alarms in this part of it (2213 only during its games), and the games are past their quiet start.
 * Meetings, an alarm already on and the gap between two are the callers' own checks.
 */
export function alarmsAllowed(state: Pick<ShowState, 'chapter' | 'phase' | 'phaseStartedAt'>, now: number): boolean {
  if (!gameInPlay(state)) return false;
  if (eraRules(state.chapter).gamesOnly && state.phase !== 'fieldwork') return false;
  return alarmQuietLeft(state, now) === 0;
}

/**
 * When a saboteur can play their sabotage, from what every screen already knows, or null when they
 * cannot this part of the era: outside the games, a meeting on, 2213 already fallen, or the stop's
 * games already done and its alarms under way. A saboteur's own phone reads it to say when its hack
 * is ready; it says nothing about who is a saboteur.
 */
export function nextSabotageAt(
  state: Pick<ShowState, 'chapter' | 'phase' | 'phaseStartedAt'> & {
    game: Pick<GameState, 'meeting' | 'uprising' | 'stop'> & { anomalies: readonly Pick<Anomaly, 'chapter' | 'at' | 'stabilisedAt' | 'fracturedAt'>[] };
  },
  now: number,
): number | null {
  // The sabotage is the saboteur's game of the stop (Marek, 2026-10-06): it can be played as soon as the
  // games open, and its alarm waits until every game is done.
  if (!gameInPlay(state) || state.phase !== 'fieldwork') return null;
  if (state.game.meeting) return null;
  if (state.chapter === MACHINE_ERA && state.game.uprising !== null) return null;
  if (state.game.stop && state.game.stop.chapter === state.chapter) return null;
  return now;
}

/**
 * This era's latest alarm: the gap between two is kept within an era, so a stop whose games come
 * quickly after the last one's alarm is not kept quiet by it.
 */
export function lastAlarmOf<A extends Pick<Anomaly, 'chapter'>>(game: { anomalies: readonly A[] }, chapter: number): A | undefined {
  for (let i = game.anomalies.length - 1; i >= 0; i--) if (game.anomalies[i]!.chapter === chapter) return game.anomalies[i];
  return undefined;
}

export function openAnomaly(game: GameState): Anomaly | null {
  return game.anomalies.find((a) => a.stabilisedAt === null && a.fracturedAt === null) ?? null;
}

export function sabotageKey(chapter: number, seat: number): string {
  return `${chapter}:${seat}`;
}

/**
 * Sabotages this seat has left this era — zero for anyone who is not a free agent. One a stop, and
 * only while the games are on: once they are done the stop's alarms go off (stop.ts).
 */
export function sabotagesLeft(state: ShowState, seat: number): number {
  const s = state.seats.find((x) => x.seat === seat);
  if (!s || s.alignment !== 'PARADOX AGENT' || s.brig) return 0;
  if (state.chapter === MACHINE_ERA && state.game.uprising !== null) return 0;
  if (state.game.stop && state.game.stop.chapter === state.chapter) return 0;
  const key = sabotageKey(state.chapter, seat);
  if (state.game.armed?.[key] !== undefined) return 0;
  const used = state.game.sabotagesUsed[key] ?? 0;
  return Math.max(0, eraRules(state.chapter).sabotagesPerChapter - used);
}

/**
 * The stretch of table a trace names. The origin is always inside it, at a position that
 * changes with every anomaly, so sitting at the end of the table is no better cover than the
 * middle.
 */
export function traceFor(state: ShowState, origin: number, n: number): [number, number] {
  const seats = Math.max(1, state.crewSize);
  // Half the table at most, so a small table's trace still narrows it down; four at eight and more.
  const width = Math.min(GAME.TRACE_WIDTH, seats, Math.max(2, Math.ceil(seats / 2)));
  const offset = mix(state.voyageId, 'trace', state.chapter, n) % width;
  const start = Math.min(Math.max(1, origin - offset), seats - width + 1);
  return [start, start + width - 1];
}

export function makeAnomaly(
  state: ShowState,
  now: number,
  origin: number,
  source: Anomaly['source'],
): Anomaly {
  const n = state.game.anomalies.length;
  const anomaly: Anomaly = {
    id: `a-${state.chapter}-${n + 1}`,
    chapter: state.chapter,
    at: now,
    near: traceFor(state, origin, n),
    // The countdown, then the puzzle's own time.
    deadline: now + GAME.FIX_COUNTDOWN_MS + eraRules(state.chapter).stabiliseWindowMs,
    holds: {},
    holdFrom: {},
    solved: {},
    need: fixersNeeded(state, state.chapter),
    called: [],
    calledAt: {},
    seen: {},
    stabilisedAt: null,
    fracturedAt: null,
    source,
  };
  return callFixers(state, anomaly, now);
}

/**
 * Whether a seat handed FIX can still answer it: aboard, not voted out, and either its phone has
 * shown the pop-up or has not yet had long enough to. A phone that has dropped off the room is
 * asleep straight away.
 */
export function fixerAnswering(state: Pick<ShowState, 'seats'>, a: Anomaly, seat: number, now: number): boolean {
  const s = state.seats.find((x) => x.seat === seat);
  if (!s || !isCrewed(s) || s.brig) return false;
  if (a.seen[String(seat)] !== undefined) return true;
  if (!s.connected) return false;
  return now - (a.calledAt[String(seat)] ?? now) < GAME.FIX_ANSWER_MS;
}

/**
 * Hand FIX to as many seats as the era needs, and to one more for every called phone that has
 * gone quiet. Nobody is ever un-called: a phone that wakes late can still help. Who is called
 * pays no attention to who is a saboteur — a saboteur handed their own alarm can hold it to look
 * innocent, or let it go and be noticed. Phones the room can reach go first.
 */
export function callFixers(state: Pick<ShowState, 'seats' | 'voyageId'>, a: Anomaly, now: number): Anomaly {
  const need = fixNeed(a);
  const live = a.called.filter((seat) => fixerAnswering(state, a, seat, now)).length;
  if (live >= need) return a;
  const order = (seat: number) => mix(state.voyageId, 'fix', a.id, seat);
  const add = state.seats
    .filter((s) => isCrewed(s) && !s.brig && !a.called.includes(s.seat))
    .sort((x, y) => Number(y.connected) - Number(x.connected) || order(x.seat) - order(y.seat))
    .slice(0, need - live)
    .map((s) => s.seat);
  if (add.length === 0) return a;
  return {
    ...a,
    called: [...a.called, ...add],
    calledAt: { ...a.calledAt, ...Object.fromEntries(add.map((seat) => [String(seat), now])) },
  };
}

/**
 * Where the timeline "comes from" when it acts for a missing agent: a real, crewed seat, so
 * the trace frames an innocent part of the table exactly as an agent's would.
 */
export function rogueOrigin(state: ShowState, k: number): number | null {
  const crew = state.seats.filter(isCrewed);
  if (crew.length === 0) return null;
  return crew[mix(state.voyageId, 'rogue', state.chapter, k) % crew.length]?.seat ?? null;
}

/**
 * The draw: at launch, at every stop after it, and when the steward draws again. Agents come only
 * from guests who said yes; everyone else who is aboard becomes crew. Those who have been a saboteur
 * fewer times go first, so each stop hands the part to someone new while there is anyone new to
 * hand it to. How many is counted from the guests playing now. Whatever the draw came up short of,
 * the timeline stands in for.
 */
export function drawAgents(state: ShowState, salt = 0): { seats: Seat[]; agents: number[]; rogue: number } {
  const want = agentsWanted(playersAboard(state));
  // Ties are shuffled anew for every evening (`drawSeed`, set at launch) and every draw (`salt`).
  const seed = state.game.drawSeed ?? 0;
  const order = (seat: number) => stir(state.voyageId, 'draw', seed, salt, seat);
  const turns = (seat: number) => state.game.agentStops?.[String(seat)]?.length ?? 0;
  const willing = state.seats
    // Never a guest voted out: they are out of the game, even when the steward draws again.
    .filter((s) => isCrewed(s) && s.volunteer === true && !s.brig)
    .sort((a, b) => turns(a.seat) - turns(b.seat) || order(a.seat) - order(b.seat));
  const agents = willing.slice(0, want).map((s) => s.seat);
  const seats = state.seats.map((s): Seat => {
    if (!isCrewed(s)) return s;
    return { ...s, alignment: agents.includes(s.seat) ? 'PARADOX AGENT' : 'PRESERVER' };
  });
  return { seats, agents, rogue: want - agents.length };
}

/** Who can still vote and be voted for: aboard, with a post, and not in the brig. */
export function meetingEligible(state: ShowState): number[] {
  return state.seats.filter((s) => isCrewed(s) && !s.brig).map((s) => s.seat);
}

/**
 * The count. Where everyone picks a suspect: the guest with the most votes is voted out, with at least
 * two votes and more than anyone else; a tie keeps everyone in. Nobody can skip since 2026-10-10; a skip
 * from a room before that still counts against the top.
 * Otherwise the named suspect goes to the brig with more yes than no.
 */
export function tallyMeeting(state: ShowState, meeting: Meeting): MeetingResult {
  if (meeting.pick) {
    const tally: Record<string, number> = { skip: 0 };
    for (const ballot of Object.values(state.votes[meeting.id] ?? {})) tally[ballot] = (tally[ballot] ?? 0) + 1;
    const counts = Object.entries(tally).filter(([k]) => k !== 'skip').sort((a, b) => b[1] - a[1]);
    const [top, second] = counts;
    const out =
      top && top[1] >= GAME.PICK_MIN_VOTES && top[1] > (second?.[1] ?? 0) && top[1] > (tally.skip ?? 0) ? Number(top[0]) : null;
    const caughtAgent = out !== null && state.seats.find((s) => s.seat === out)?.alignment === 'PARADOX AGENT';
    return { sentToBrig: out, caughtAgent, tally };
  }
  const ballots = Object.values(state.votes[meeting.id] ?? {});
  const yes = ballots.filter((b) => b === 'yes').length;
  const no = ballots.filter((b) => b === 'no').length;
  // More yes than no among the votes cast. A tie, or a table that stays silent, keeps the suspect in.
  const sentToBrig = typeof meeting.suspect === 'number' && yes > no ? meeting.suspect : null;
  const caughtAgent =
    sentToBrig !== null &&
    state.seats.find((s) => s.seat === sentToBrig)?.alignment === 'PARADOX AGENT';
  return { sentToBrig, caughtAgent, tally: { yes, no } };
}

/**
 * Whether a guest may call a meeting now: an alarm this era has shown where it came from, and it is
 * still on or ended less than two minutes ago. A meeting follows a trace, so nobody is put on the
 * spot out of nowhere, and nobody has to stand up and argue for one.
 */
export function meetingUnlocked(
  state: { chapter: number; game: { anomalies: readonly Pick<Anomaly, 'chapter' | 'stabilisedAt' | 'fracturedAt'>[] } },
  now: number,
): boolean {
  return state.game.anomalies.some((a) => {
    if (a.chapter !== state.chapter) return false;
    const ended = a.stabilisedAt ?? a.fracturedAt;
    return ended === null || now - ended <= GAME.MEETING_AFTER_ALARM_MS;
  });
}

/** The voyage decided at docking: the ship home, whatever the damage. Nobody wins or loses. */
export function gameOutcome(_game: GameState): 'home' {
  return 'home';
}
